/**
 * Children / dependents — shared view + editor used by the admin user form and
 * the profile views. Each child has a name, last name, date of birth, gender,
 * and AHV number. Self-editable on /profile (via ChildrenModal) when the
 * `children` field is visible to the user; also admin-managed in the UserForm.
 */
import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import {
  CALLABLES,
  GENDERS,
  type Child,
  type SelfUpdateProfileInput,
  type SelfUpdateProfileResponse,
} from '@daaily/shared';
import { Modal } from '../components/Modal';
import { useFireAndForget } from '../components/BackgroundSubmit';

/** Drop incomplete rows + trim. A child needs at least a first name. */
export function cleanChildren(list: Child[]): Child[] {
  return list
    .map((c) => ({
      name: c.name.trim(),
      lastName: c.lastName.trim(),
      dateOfBirth: c.dateOfBirth.trim(),
      gender: c.gender ?? null,
      ahvNumber: c.ahvNumber.trim(),
    }))
    .filter((c) => c.name);
}

const EMPTY: Child = { name: '', lastName: '', dateOfBirth: '', gender: null, ahvNumber: '' };

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

// ── Read-only view ──────────────────────────────────────────────────────────
export function ChildrenView({ items }: { items: Child[] }) {
  if (!items || items.length === 0) {
    return <p className="text-sm text-fg-faint">None added.</p>;
  }
  return (
    <div className="space-y-3">
      {items.map((c, i) => (
        <div key={i} className="border border-line rounded-md p-3">
          <div className="text-sm font-medium text-fg">
            {c.name} {c.lastName}
            {c.gender ? <span className="text-fg-faint font-normal"> · {cap(c.gender)}</span> : null}
          </div>
          <dl className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
            {c.dateOfBirth && (
              <div className="flex justify-between gap-3">
                <dt className="text-fg-faint">Date of birth</dt>
                <dd className="text-fg">{c.dateOfBirth}</dd>
              </div>
            )}
            {c.ahvNumber && (
              <div className="flex justify-between gap-3">
                <dt className="text-fg-faint">AHV number</dt>
                <dd className="text-fg">{c.ahvNumber}</dd>
              </div>
            )}
          </dl>
        </div>
      ))}
    </div>
  );
}

// ── Editor ──────────────────────────────────────────────────────────────────
export function ChildrenEditor({
  value,
  onChange,
  max = 20,
}: {
  value: Child[];
  onChange: (next: Child[]) => void;
  max?: number;
}) {
  const set = (i: number, patch: Partial<Child>) =>
    onChange(value.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  const remove = (i: number) => onChange(value.filter((_, idx) => idx !== i));
  const add = () => onChange([...value, { ...EMPTY }]);

  return (
    <div className="space-y-4">
      {value.map((c, i) => (
        <div key={i} className="border border-line rounded-md p-3 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs text-fg-faint">Child {i + 1}</span>
            <button type="button" onClick={() => remove(i)} className="text-xs text-fg-faint hover:text-fg">
              Remove
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-fg-faint mb-1">Name</label>
              <input type="text" value={c.name} onChange={(e) => set(i, { name: e.target.value })} maxLength={140} className="w-full" placeholder="First name" />
            </div>
            <div>
              <label className="block text-xs text-fg-faint mb-1">Last name</label>
              <input type="text" value={c.lastName} onChange={(e) => set(i, { lastName: e.target.value })} maxLength={140} className="w-full" placeholder="Last name" />
            </div>
            <div>
              <label className="block text-xs text-fg-faint mb-1">Date of birth</label>
              <input type="date" value={c.dateOfBirth} onChange={(e) => set(i, { dateOfBirth: e.target.value })} className="w-full" />
            </div>
            <div>
              <label className="block text-xs text-fg-faint mb-1">Gender</label>
              <select value={c.gender ?? ''} onChange={(e) => set(i, { gender: (e.target.value || null) as Child['gender'] })} className="w-full">
                <option value="">— not set —</option>
                {GENDERS.map((g) => (
                  <option key={g} value={g}>{cap(g)}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs text-fg-faint mb-1">AHV number</label>
              <input type="text" value={c.ahvNumber} onChange={(e) => set(i, { ahvNumber: e.target.value })} maxLength={40} className="w-full" placeholder="756.xxxx.xxxx.xx" />
            </div>
          </div>
        </div>
      ))}
      {value.length < max && (
        <button type="button" onClick={add} className="btn-secondary text-sm">
          + Add child
        </button>
      )}
    </div>
  );
}

// ── Self-service modal ────────────────────────────────────────────────────────
// Partial input — only sends `children`; the callable treats absent groups as
// unchanged and rejects when the field isn't visible to the user.
const callSelfUpdate = httpsCallable<Partial<SelfUpdateProfileInput>, SelfUpdateProfileResponse>(
  functions,
  CALLABLES.SELF_UPDATE_PROFILE,
);

export function ChildrenModal({
  initial,
  onClose,
}: {
  initial: Child[];
  onClose: () => void;
}) {
  const [list, setList] = useState<Child[]>(initial.length ? initial.map((c) => ({ ...c })) : [{ ...EMPTY }]);
  const fireAndForget = useFireAndForget();

  // Close at once and save in the background (the profile page is live from Firestore, so it
  // updates itself). The payload is captured, so Retry works after this modal has unmounted.
  const save = () => {
    const payload = cleanChildren(list);
    const fire = () => fireAndForget(
      () => callSelfUpdate({ children: payload }),
      { label: 'Saving your children', onRetry: fire },
    );
    fire();
    onClose();
  };

  return (
    <Modal open onClose={onClose} title="Edit children">
      <p className="text-xs text-fg-faint mb-4">Your children / dependents.</p>

      <ChildrenEditor value={list} onChange={setList} />

      <div className="mt-6 flex items-center justify-end gap-3">
        <button type="button" onClick={onClose} className="btn-secondary text-sm">
          Cancel
        </button>
        <button type="button" onClick={save} className="btn-primary text-sm">
          Save changes
        </button>
      </div>
    </Modal>
  );
}
